import type { ReactNode, SVGProps } from "react";

/**
 * Transport chrome glyphs, her paths (`l3.js` `UI.P`): route, wheelchair,
 * steering wheel, phone, pin, bag, spark. 24-unit boxes, 1.8 stroke, round
 * caps, `currentColor`; sizes come from the caller in rem. Vehicle-type
 * glyphs are S5's (`VehicleTypeIcon` in `transport-fleet`); direction and
 * mode glyphs are M9's (`travel-leg-form`).
 */
type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

function Glyph({ children, ...props }: P & { children: ReactNode }) {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false} {...props}>
			{children}
		</svg>
	);
}

export const RouteIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="6" cy="18" r="2" />
		<circle cx="18" cy="6" r="2" />
		<path d="M8 18h7.5a3.5 3.5 0 0 0 0-7h-7a3.5 3.5 0 0 1 0-7H16" />
	</Glyph>
);

export const WheelchairIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="11" cy="15" r="5" />
		<circle cx="10" cy="4.5" r="1.5" />
		<path d="M10 7v6.5h6l2.5 5" />
	</Glyph>
);

export const SteerIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<circle cx="12" cy="12" r="2" />
		<path d="M3.8 10.5 10 12M20.2 10.5 14 12M12 14v6.5" />
	</Glyph>
);

export const PhoneIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M6.5 3.5h3l1.5 4-2 1.5a11 11 0 0 0 6 6l1.5-2 4 1.5v3a1.5 1.5 0 0 1-1.6 1.5A17 17 0 0 1 5 5.1 1.5 1.5 0 0 1 6.5 3.5Z" />
	</Glyph>
);

export const PinIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 20.5s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11Z" />
		<circle cx="12" cy="9.5" r="2.3" />
	</Glyph>
);

export const BagIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="5" y="7.5" width="14" height="12" rx="2" />
		<path d="M9 7.5v-2A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5v2M9 11v5M15 11v5" />
	</Glyph>
);

/** Her "spark" — a suggestion the system made (Suggested transfers, Suggest). */
export const SparkIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 3.5 13.8 10.2 20.5 12l-6.7 1.8L12 20.5l-1.8-6.7L3.5 12l6.7-1.8Z" />
	</Glyph>
);

export const ZapIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M13 3 5 13.5h6L10 21l8-10.5h-6Z" />
	</Glyph>
);

export const ClockIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<path d="M12 7.5V12l3 2" />
	</Glyph>
);

export const AlertIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M10.3 4.2 2.9 17.5A2 2 0 0 0 4.6 20.5h14.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
		<path d="M12 9.5v4M12 17h.01" />
	</Glyph>
);

export const InfoIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<path d="M12 11v5M12 8h.01" />
	</Glyph>
);

export const CheckIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m5 12.5 4.5 4.5L19 7.5" />
	</Glyph>
);

export const PlusIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 5v14M5 12h14" />
	</Glyph>
);

export const TrashIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 12.5h9l1-12.5M10 11v5M14 11v5" />
	</Glyph>
);

export const CloseIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M6 6l12 12M18 6 6 18" />
	</Glyph>
);

export const PencilIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17Z" />
		<path d="m14.5 7.5 3 3" />
	</Glyph>
);

export const MergeIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M6 4v4a6 6 0 0 0 6 6h0a6 6 0 0 1 6 6M18 4v4a6 6 0 0 1-6 6" />
		<path d="M12 14v6" />
	</Glyph>
);

export const ArrowRightIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M5 12h14M13 6l6 6-6 6" />
	</Glyph>
);

export const ChevronRightIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m9 6 6 6-6 6" />
	</Glyph>
);

export const ChevronLeftIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m15 6-6 6 6 6" />
	</Glyph>
);

export const PrinterIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M7 9V4.5h10V9" />
		<rect x="4" y="9" width="16" height="7.5" rx="1.8" />
		<path d="M7 14h10v5.5H7Z" />
	</Glyph>
);

export const HistoryIcon = ClockIcon;
